import { useLocalStorageState } from 'ahooks';
import { useEffect, useMemo, useState } from 'react';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';

type SetterParams<T> = T | ((prev: T) => T);
type Setter<T> = (state: SetterParams<T>) => any;

export const PAY_TYPE_ITEMS: Pay.PayType[] = [
  'alipay',
  'airwallex',
  'wxpay',
  'pingpong',
  'lianlian',
  'stripe',
  'asaas',
];

export const supportCNYPayTypes: Pay.PayType[] = ['alipay', 'wxpay'];
const supportOtherPayTypes: Pay.PayType[] = ['pingpong', 'airwallex'];

export const getAvailablePayTypes = (
  currency: General.Currency,
  asaasEnabled = false,
): Pay.PayType[] => {
  if (['PHP', 'MYR'].includes(currency)) {
    return ['lianlian'];
  }

  const types = [...(currency === 'CNY' ? supportCNYPayTypes : supportOtherPayTypes)];
  const filtered = ['MXN', 'THB', 'USD', 'VND'].includes(currency)
    ? types.filter((type) => type !== 'pingpong')
    : types;
  return currency === 'BRL' && asaasEnabled ? ['asaas', ...filtered] : filtered;
};

export const getAutoRenewPayTypes = (
  currency: General.Currency,
  asaasSubscriptionEnabled = false,
): Pay.PayType[] =>
  currency === 'BRL' && asaasSubscriptionEnabled ? ['asaas', 'stripe'] : ['stripe'];

export type SubscriptionPayType = Extract<Pay.PayType, 'asaas' | 'stripe'>;

export const getSubscriptionPayType = (
  paymentChannel?: string,
): SubscriptionPayType | undefined => {
  const channel = paymentChannel?.toUpperCase();
  if (channel === 'ASAAS') return 'asaas';
  if (channel === 'STRIPE') return 'stripe';
  return undefined;
};

export const useAsaasEnabled = () => {
  const { whiteListConfig } = useOrgConfigStateValue();
  return whiteListConfig?.ASAAS_ENABLED === true;
};

export const useAsaasSubscriptionEnabled = () => {
  const { whiteListConfig } = useOrgConfigStateValue();
  return (
    whiteListConfig?.ASAAS_ENABLED === true && whiteListConfig?.ASAAS_SUBSCRIPTION_ENABLED === true
  );
};

export const useAutoRenewPayType = (currency: General.Currency) => {
  const asaasSubscriptionEnabled = useAsaasSubscriptionEnabled();
  const autoRenewPayTypes = useMemo(
    () => getAutoRenewPayTypes(currency, asaasSubscriptionEnabled),
    [currency, asaasSubscriptionEnabled],
  );
  const [payType, setPayType] = useState<Pay.PayType>('stripe');

  useEffect(() => {
    setPayType(autoRenewPayTypes[0]);
  }, [autoRenewPayTypes]);

  const selectedPayType = autoRenewPayTypes.includes(payType) ? payType : autoRenewPayTypes[0];
  return {
    autoRenewPayTypes,
    selectedAutoRenewPayType: selectedPayType,
    setAutoRenewPayType: setPayType,
  };
};

const usePayTypeState = (availablePayTypes: Pay.PayType[], cacheKey = '@fett/default-pay/type') => {
  const [payType, setPayType] = useLocalStorageState<Pay.PayType>(cacheKey, {
    defaultValue: () => {
      return 'alipay';
    },
    serializer: (v) => v ?? 'alipay',
    deserializer: (v) => {
      return PAY_TYPE_ITEMS.find((t) => t === v) || 'alipay';
    },
  });
  useEffect(() => {
    if (availablePayTypes[0] === 'asaas') {
      setPayType((current) => (current === 'asaas' ? current : 'asaas'));
    }
  }, [availablePayTypes, setPayType]);
  return useMemo(() => {
    let finalPayType = payType || 'alipay';

    if (!availablePayTypes.includes(payType)) {
      finalPayType = availablePayTypes[0];
    }

    return [finalPayType, setPayType as Setter<Pay.PayType>] as const;
  }, [payType, setPayType, availablePayTypes]);
};

export const useAvailablePayTypes = (currency: General.Currency) => {
  const asaasEnabled = useAsaasEnabled();
  return useMemo(() => getAvailablePayTypes(currency, asaasEnabled), [currency, asaasEnabled]);
};

export default usePayTypeState;
